import { useState, useEffect } from "react";

import { useDispatch } from 'react-redux'
import { GetStaticContent } from "../../features/auth/authSlice";
import FooterStatic from "../../components/FooterStatic";
import { toast } from 'react-hot-toast';
import ShimmerLoader from "../../components/ShimmerLoader";
import {
  Shield,
  Plane,
  Clock,
  Wallet,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Phone,
  Mail,
  MessageSquare,
  Scale,
  Lock,
  FileText,
  UserCheck,
  Ban,
  Gavel,
  Settings,
  Star,
} from "lucide-react";

const serviceInfo = [
  { label: "Service Name", value: "Goertrip Care+ — Flight Only" },
  { label: "Price", value: "₹199 per passenger (per booking)" },
  {
    label: "Coverage Scope",
    value:
      "Flight Cancellations, Delays, Schedule Changes, Web Check-in, Visa Assistance, Instant Refund Support",
  },
  {
    label: "Resolution Promise",
    value: "Best effort resolution within 30 minutes* of issue receipt",
  },
  {
    label: "Refund Type",
    value: "Wallet Refund — Instant (subject to airline/bank processing)",
  },
  {
    label: "Effective Date",
    value: "Upon successful add-on purchase at checkout",
  },
  {
    label: "Governing Law",
    value: "India — Consumer Protection Act 2019 & applicable aviation regulations",
  },
];

const coveredEvents = [
  {
    title: "Flight Cancellations",
    desc: "We assist with rebooking or refund options when your flight is cancelled by the airline.",
  },
  {
    title: "Flight Delays",
    desc: "Real-time updates and swift solutions for delayed departures or arrivals.",
  },
  {
    title: "Schedule Changes",
    desc: "Assistance finding alternate flight options when the airline modifies your schedule.",
  },
  {
    title: "Web Check-in Assistance",
    desc: "End-to-end check-in handling so you do not need to manage it yourself.",
  },
  {
    title: "Visa Assistance",
    desc: "Guidance on visa requirements, documentation checklists, and application support (approval at embassy discretion).",
  },
  {
    title: "Instant Refund Support",
    desc: "Wallet refunds initiated within minutes for eligible cancellations.",
  },
];

const exclusions = [
  "Cancellations or changes initiated by the passenger (voluntary changes).",
  "Missed flights due to late arrival at the airport by the passenger.",
  "Flight disruptions caused by force majeure events (natural disasters, war, pandemics, government orders).",
  "Baggage-related claims, loss, or damage.",
  "Hotel, accommodation, or ground transport costs arising from disruptions.",
  "Disruptions on connecting flights booked outside of Goertrip's platform.",
  "Visa rejection — Visa approval is solely at the discretion of the embassy or consulate.",
  "Medical or health-related travel disruptions.",
];

const sections = [
  {
    id: 1,
    icon: <FileText size={20} />,
    title: "Introduction & Acceptance",
    content: (
      <p className="text-slate-600 leading-relaxed">
        These Terms and Conditions (<span className="font-semibold text-slate-800">'Terms'</span>) govern the{" "}
        <span className="font-semibold text-sky-700">Goertrip Care+ Flight Only</span> service ('Service') offered by Goertrip
        ('Company', 'We', 'Us'). By purchasing or activating this Service, the passenger ('You', 'Customer') agrees to be
        legally bound by these Terms.
        <br />
        <br />
        Please read these Terms carefully before completing your purchase. If you do not agree with any part of these Terms,
        you should not purchase the Service.
      </p>
    ),
  },
  {
    id: 2,
    icon: <Shield size={20} />,
    title: "Service Description",
    content: (
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800 text-sm leading-relaxed">
            Goertrip Care+ is a flight disruption assistance add-on providing real-time support, rebooking assistance, and
            instant wallet refunds for covered events. This is{" "}
            <span className="font-bold">NOT a travel insurance product.</span> It is a customer support facilitation service.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-500" />
            2.1 Covered Events
          </h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {coveredEvents.map((item) => (
              <div
                key={item.title}
                className="bg-emerald-50 border border-emerald-100 rounded-xl p-4"
              >
                <p className="font-semibold text-emerald-800 text-sm mb-1">{item.title}</p>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <XCircle size={16} className="text-rose-500" />
            2.2 Coverage Exclusions
          </h4>
          <ul className="space-y-2">
            {exclusions.map((ex, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-600">
                <XCircle size={15} className="text-rose-400 mt-0.5 shrink-0" />
                {ex}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    icon: <Wallet size={20} />,
    title: "Pricing and Payment",
    content: (
      <ul className="space-y-3">
        {[
          "The Service is priced at ₹199 per passenger (per PAX) per booking.",
          "The Service must be added at checkout before completing the booking. It cannot be added retrospectively.",
          "The Service fee is non-refundable once added, except as required by applicable law.",
          "GST and other applicable taxes will be charged in addition to the stated price, as per prevailing tax laws.",
        ].map((item, i) => (
          <li key={i} className="flex gap-2 text-slate-600 text-sm leading-relaxed">
            <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
              {i + 1}
            </span>
            {item}
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: 4,
    icon: <Clock size={20} />,
    title: "The 30-Minute Resolution Promise",
    content: (
      <div className="space-y-5">
        <p className="text-slate-600 text-sm leading-relaxed">
          Goertrip promises to initiate a resolution process within{" "}
          <span className="font-bold text-sky-700">30 minutes</span> of receiving your disruption request via the app or
          support channel.
        </p>

        <div>
          <h4 className="font-semibold text-slate-800 mb-3 text-sm">4.1 What the Promise Covers</h4>
          <div className="space-y-2">
            {[
              { time: "5 min", label: "Acknowledgment of your issue after submission." },
              { time: "10 min", label: "A support agent assigned and working on your case." },
              { time: "30 min", label: "A proposed solution or update communicated to you." },
            ].map((item) => (
              <div key={item.time} className="flex items-center gap-3 bg-sky-50 border border-sky-100 rounded-xl p-3">
                <span className="bg-sky-600 text-white text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap">
                  Within {item.time}
                </span>
                <p className="text-slate-700 text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 mb-3 text-sm">4.2 Limitations & Disclaimers</h4>
          <p className="text-slate-500 text-sm mb-2">
            The 30-minute resolution promise is made on a best-effort basis and is subject to the following:
          </p>
          <ul className="space-y-2">
            {[
              "Airline availability, policies, and real-time seat inventory at the time of disruption.",
              "High-volume disruption events (e.g., mass cancellations due to weather) where airline systems may be overwhelmed.",
              "Requests submitted outside of Goertrip's active support hours.",
              "Technical outages beyond Goertrip's control.",
            ].map((item, i) => (
              <li key={i} className="flex gap-2 text-slate-600 text-sm">
                <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-slate-500 text-xs mt-3 italic">
            Goertrip shall not be held liable for failure to resolve within 30 minutes where such delay is caused by factors
            outside its direct control, including airline response times and third-party system outages.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 5,
    icon: <Wallet size={20} />,
    title: "Refund Policy",
    content: (
      <div className="space-y-5">
        <div>
          <h4 className="font-semibold text-slate-800 mb-2 text-sm">5.1 Instant Wallet Refund</h4>
          <p className="text-slate-500 text-xs mb-3">
            For eligible flight cancellations where the airline confirms the cancellation and approves the refund:
          </p>
          <ul className="space-y-2">
            {[
              "Goertrip will initiate a wallet refund to your Goertrip account balance within minutes of confirmation.",
              "This wallet credit can be used for future bookings on the Goertrip platform.",
              "Bank/original payment method refunds are subject to respective airline and bank processing timelines.",
            ].map((item, i) => (
              <li key={i} className="flex gap-2 text-slate-600 text-sm">
                <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 mb-2 text-sm">5.2 Standard Airline Refunds</h4>
          <p className="text-slate-500 text-xs mb-3">
            Where the airline does not offer instant refund options, or where the passenger opts for original payment method refund:
          </p>
          <ul className="space-y-2">
            {[
              "Refunds may take up to 14 working days depending on the airline and bank processing timelines.",
              "Goertrip will facilitate the refund request but cannot guarantee airline processing speed.",
              "Goertrip is not liable for delays caused by the airline or banking institutions.",
            ].map((item, i) => (
              <li key={i} className="flex gap-2 text-slate-600 text-sm">
                <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 mb-2 text-sm">5.3 Non-Refundable Situations</h4>
          <ul className="space-y-2">
            {[
              "Service fee (₹199) is non-refundable once activated.",
              "Refunds will not be processed for disruptions caused by passenger error or voluntary changes.",
              "Refunds will not be issued for no-show situations.",
            ].map((item, i) => (
              <li key={i} className="flex gap-2 text-slate-600 text-sm">
                <XCircle size={14} className="text-rose-400 mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 6,
    icon: <Globe size={20} />,
    title: "Visa Assistance — Important Disclaimer",
    content: (
      <div className="space-y-4">
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle size={18} className="text-rose-600 mt-0.5 shrink-0" />
          <p className="text-rose-800 text-sm leading-relaxed">
            The Visa Assistance component is strictly a documentation guidance and checklist service. Goertrip is{" "}
            <span className="font-bold">NOT a licensed immigration advisor or visa agency.</span>
          </p>
        </div>
        <ul className="space-y-2">
          {[
            "Goertrip provides visa requirement information on a best-effort basis and does not guarantee the accuracy, completeness, or currency of visa information.",
            "Visa approvals are entirely at the discretion of the relevant embassy, consulate, or immigration authority.",
            "Goertrip shall bear no responsibility for visa rejections, delays, or complications arising from the use of this Service.",
            "Passengers should independently verify visa requirements with the official embassy or consulate of the destination country.",
          ].map((item, i) => (
            <li key={i} className="flex gap-2 text-slate-600 text-sm leading-relaxed">
              <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 7,
    icon: <UserCheck size={20} />,
    title: "Customer Obligations",
    content: (
      <ul className="space-y-3">
        {[
          "Notify Goertrip of any flight disruption through the Goertrip app or designated support channel within 4 hours of the disruption event.",
          "Provide accurate and complete booking and flight details when raising a support request.",
          "Remain available on the registered contact number/email during the resolution process.",
          "Cooperate with Goertrip's support team and provide any additional documentation requested within a reasonable timeframe.",
          "Not engage directly with the airline in parallel for the same issue without informing Goertrip, as this may affect the resolution process.",
        ].map((item, i) => (
          <li key={i} className="flex gap-2 text-slate-600 text-sm leading-relaxed">
            <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
              {i + 1}
            </span>
            {item}
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: 8,
    icon: <Scale size={20} />,
    title: "Limitation of Liability",
    content: (
      <div className="space-y-3">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-500 italic">
          To the maximum extent permitted under applicable law:
        </div>
        <ul className="space-y-2">
          {[
            "Goertrip's total liability to the Customer under this Service shall not exceed the cost of the Service (₹199 per PAX) paid by the Customer.",
            "Goertrip is not liable for any indirect, consequential, incidental, or punitive damages, including but not limited to missed events, accommodation costs, connecting flight losses, or business losses.",
            "Goertrip does not guarantee specific outcomes such as exact seat preferences, flight times, or routes in rebooking scenarios.",
            "Goertrip's liability is strictly limited to facilitation of support services and does not extend to airline decisions, refund quantum, or government/regulatory actions.",
          ].map((item, i) => (
            <li key={i} className="flex gap-2 text-slate-600 text-sm leading-relaxed">
              <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 9,
    icon: <Lock size={20} />,
    title: "Data Privacy and Usage",
    content: (
      <div className="space-y-3">
        <p className="text-slate-500 text-sm">By purchasing Goertrip Care+, you consent to the following:</p>
        <ul className="space-y-2">
          {[
            "Goertrip will access and process your booking details, flight information, and contact details for the sole purpose of providing the Service.",
            "Your personal data will be shared with relevant airlines, third-party service providers, and payment gateways strictly as required to deliver the Service.",
            "Data will be handled in accordance with Goertrip's Privacy Policy, available on the Goertrip website and app.",
            "Goertrip will not sell your personal data to third parties for marketing purposes.",
          ].map((item, i) => (
            <li key={i} className="flex gap-2 text-slate-600 text-sm leading-relaxed">
              <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 10,
    icon: <Settings size={20} />,
    title: "Modifications to Terms",
    content: (
      <p className="text-slate-600 text-sm leading-relaxed">
        Goertrip reserves the right to modify these Terms at any time. Material changes will be communicated via the Goertrip
        app or email at least <span className="font-semibold text-slate-800">7 days</span> before they take effect. Continued
        use of the Service after the effective date constitutes acceptance of the revised Terms.
      </p>
    ),
  },
  {
    id: 11,
    icon: <Ban size={20} />,
    title: "Service Cancellation",
    content: (
      <div className="space-y-4">
        <p className="text-slate-600 text-sm leading-relaxed">
          Goertrip reserves the right to suspend or terminate the Service for a Customer in the following circumstances:
        </p>
        <ul className="space-y-2">
          {[
            "Fraudulent, abusive, or misrepresented claims.",
            "Repeated misuse of the support system, including filing false disruption claims.",
            "Violation of any of these Terms.",
          ].map((item, i) => (
            <li key={i} className="flex gap-2 text-slate-600 text-sm">
              <XCircle size={14} className="text-rose-400 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-slate-500 text-xs italic bg-slate-50 rounded-xl p-3 border border-slate-200">
          In the event of termination due to fraud or abuse, no refund of the Service fee will be issued.
        </p>
      </div>
    ),
  },
  {
    id: 12,
    icon: <Gavel size={20} />,
    title: "Dispute Resolution & Grievance Redressal",
    content: (
      <div className="space-y-5">
        <div>
          <h4 className="font-semibold text-slate-800 text-sm mb-3">12.1 Customer Support</h4>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 flex flex-col gap-2">
              <MessageSquare size={18} className="text-sky-600" />
              <p className="font-semibold text-slate-800 text-sm">In-app Support Chat</p>
              <p className="text-slate-500 text-xs">Available 24/7</p>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 flex flex-col gap-2">
              <Mail size={18} className="text-sky-600" />
              <p className="font-semibold text-slate-800 text-sm">Email</p>
              <p className="text-slate-500 text-xs">support@goertrip.com</p>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 flex flex-col gap-2">
              <Phone size={18} className="text-sky-600" />
              <p className="font-semibold text-slate-800 text-sm">Helpline</p>
              <p className="text-slate-500 text-xs">As displayed on the Goertrip app/website</p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 text-sm mb-2">12.2 Grievance Officer</h4>
          <p className="text-slate-600 text-sm leading-relaxed">
            In accordance with the Consumer Protection Act, 2019, a Grievance Officer has been designated. Details are
            available on the Goertrip website. Grievances will be acknowledged within{" "}
            <span className="font-semibold text-slate-800">48 hours</span> and resolved within{" "}
            <span className="font-semibold text-slate-800">30 days.</span>
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 text-sm mb-2">12.3 Governing Law & Jurisdiction</h4>
          <p className="text-slate-600 text-sm leading-relaxed">
            These Terms are governed by the laws of India. Any disputes arising out of these Terms shall be subject to the
            exclusive jurisdiction of the courts in <span className="font-semibold text-slate-800">Gurgaon, India.</span>
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 13,
    icon: <FileText size={20} />,
    title: "General Provisions",
    content: (
      <ul className="space-y-3">
        {[
          "If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force.",
          "These Terms constitute the entire agreement between Goertrip and the Customer for the Service described herein.",
          "Failure by Goertrip to enforce any right under these Terms shall not constitute a waiver of that right.",
          "These Terms are available in English. In the event of any conflict between translated versions, the English version shall prevail.",
        ].map((item, i) => (
          <li key={i} className="flex gap-2 text-slate-600 text-sm leading-relaxed">
            <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    ),
  },
];

function Section({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
            {section.icon}
          </span>
          <span className="font-semibold text-slate-800 text-left text-sm sm:text-base">
            <span className="text-sky-600 mr-1">{section.id}.</span>
            {section.title}
          </span>
        </div>
        {open ? (
          <ChevronUp size={18} className="text-slate-400 shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-slate-400 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-2 border-t border-slate-100">{section.content}</div>
      )}
    </div>
  );
}
export default function GoerPlusTnc() {

  const dispatch = useDispatch()
  const [Processing, setProcessing] = useState(true);
  const [PageData, setPageData] = useState([]);

  return (
    <>

      <div className="font-sans text-gray-800">

        <section className="bg-white px-0 md:px-6 py-8 mb-4">
          <div className="container mx-auto px-0 md:px-4 flex flex-wrap items-center">
            <div className="w-full">
              <h2 className="text-2xl font-bold mb-4 px-4">Terms & Conditions</h2>
              <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white font-sans">
                {/* Header */}
                <div className="bg-gradient-to-r rounded-lg from-sky-700 to-sky-500 text-white px-4 pt-10 pb-16">
                  <div className="w-full mx-auto">
                    <div className="flex items-center gap-2 mb-2">
                      <Plane size={18} className="opacity-80" />
                      <span className="text-sky-200 text-xs font-medium uppercase tracking-widest">Flight Only Plan</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold mb-1 tracking-tight">
                      Goertrip <span className="text-sky-200">Care+</span>
                    </h1>
                    <p className="text-sky-100 text-sm mb-5">Terms &amp; Conditions</p>
                    <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2 w-fit">
                      <Star size={14} className="text-yellow-300 fill-yellow-300" />
                      <span className="text-white text-sm font-medium">₹199 per passenger · Flight Disruption Assistance</span>
                    </div>
                  </div>
                </div>

                <div className="w-full mx-auto px-4 -mt-8 pb-16">
                  {/* Service Summary Card */}
                  <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden mb-6">
                    <div className="bg-sky-50 border-b border-sky-100 px-5 py-3 flex items-center gap-2">
                      <Shield size={16} className="text-sky-600" />
                      <h2 className="font-semibold text-sky-800 text-sm">Service Summary</h2>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {serviceInfo.map(({ label, value }) => (
                        <div key={label} className="flex gap-3 px-5 py-3">
                          <span className="text-slate-400 text-xs font-medium w-36 shrink-0 pt-0.5">{label}</span>
                          <span className="text-slate-700 text-xs leading-relaxed">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Important Disclaimers Banner */}
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 space-y-2">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle size={16} className="text-amber-600" />
                      <span className="font-semibold text-amber-800 text-sm">Important Disclaimers</span>
                    </div>
                    {[
                      "30-minute resolution is on a best-effort basis, subject to airline availability and policies.",
                      "Refund timelines are subject to airline and bank processing times.",
                      "Visa approval is at the sole discretion of the relevant embassy or consulate.",
                    ].map((d, i) => (
                      <p key={i} className="text-amber-700 text-xs leading-relaxed flex gap-2">
                        <span className="mt-0.5">*</span>
                        {d}
                      </p>
                    ))}
                  </div>

                  {/* Sections */}
                  <div className="space-y-3">
                    {sections.map((section) => (
                      <Section key={section.id} section={section} />
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="mt-10 text-center space-y-1">
                    <p className="text-slate-400 text-xs">This document is effective as of the date of purchase of Goertrip Care+.</p>
                    <p className="text-slate-400 text-xs">Version 1.0</p>
                    <div className="flex items-center justify-center gap-1 mt-3">
                      <Plane size={14} className="text-sky-400" />
                      <span className="text-sky-600 text-xs font-medium">Goertrip Care+ · Travel More. Worry Less.</span>
                    </div>
                    <a
                      href="mailto:support@goertrip.com"
                      className="text-sky-500 text-xs hover:underline flex items-center justify-center gap-1 mt-1"
                    >
                      <Mail size={12} />
                      support@goertrip.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterStatic />
      </div>

    </>
  );
}

